import { useState } from 'react';

import { SettingsAdminsEndpoint } from '@core/types/types.endpoint.settings';
import { isAdmin } from '@core/util/util.isEditor';
import { Button } from '@frontend/components/ui/button';
import { UserField } from '@frontend/shared/components/UserField/UserField';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import {
  useDirectoryTeam,
  useDirectoryUsers,
} from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

interface SettingsAdminsProps {
  isReadOnly?: boolean;
}

export function SettingsAdmins({ isReadOnly = false }: SettingsAdminsProps) {
  const users = useDirectoryUsers();
  const team = useDirectoryTeam();
  const updater = useDirectoryUpdater();

  // Get current admin IDs from team data
  const adminIds = users
    .filter((user) => isAdmin(team, user.id))
    .map(({ id }) => id);

  const [selectedIds, setSelectedIds] = useState(adminIds);

  const isDifferent =
    adminIds.length !== selectedIds.length ||
    selectedIds.filter((id) => !adminIds.includes(id)).length > 0;

  const submitter = useSubmitter(async () => {
    const [, res] = await callApi<SettingsAdminsEndpoint>('/settings/admins', {
      adminIds: selectedIds,
    });
    if (res) {
      updater({
        team: res,
      });
    }
  });

  return (
    <div>
      <h3 className="text-xs font-semibold">Administrators</h3>
      <p className="mt-1 text-xs font-medium text-gray-500">
        Can manage settings, users, and permissions. Have Editor rights.
      </p>
      {!isReadOnly && (
        <div className="max-w-[540px]">
          <UserField
            isDisabled={submitter.isSubmitting}
            placeholder="+Add Administrator"
            excludeDeleted
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        </div>
      )}
      {isReadOnly && (
        <div className="mt-2">
          {adminIds.map((id) => {
            const user = users.find((u) => u.id === id);
            return user ? (
              <p key={id} className="mb-1 text-sm">
                {user.name}
              </p>
            ) : null;
          })}
        </div>
      )}

      {!isReadOnly && isDifferent && (
        <div className="mt-4 flex">
          <Button
            size="sm"
            disabled={!selectedIds.length || submitter.isSubmitting}
            onClick={submitter.submit}
          >
            {submitter.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={() => setSelectedIds(adminIds)}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
